import type { ImpersonationMode, User } from '@court-sync/shared';
import { mergeClientRoles } from '@court-sync/shared';
import { resolvePlatformDashboardRedirect } from '../utils/platformLoginRedirect';
import { adminBackofficeApi } from './adminBackoffice';
import { useAuthStore } from '../store/auth';
import { useImpersonationStore } from '../store/impersonation';

function mapProfileToUser(profile: Record<string, unknown>): User {
  const rolesWithAcademy = (profile.rolesWithAcademy as User['rolesWithAcademy']) ?? [];
  const roles = mergeClientRoles(
    Array.isArray(profile.roles) ? (profile.roles as string[]) : undefined,
    rolesWithAcademy
  );

  const createdAtRaw = profile.createdAt;
  const updatedAtRaw = profile.updatedAt;

  return {
    id: String(profile.id),
    uid: String(profile.uid ?? profile.id),
    email: String(profile.email ?? ''),
    firstName: String(profile.firstName ?? 'User'),
    lastName: String(profile.lastName ?? ''),
    academyId: profile.academyId as string | undefined,
    roles,
    rolesWithAcademy: rolesWithAcademy.length > 0 ? rolesWithAcademy : undefined,
    hasAcademyProfile: Boolean(profile.hasAcademyProfile),
    isActive: Boolean(profile.isActive ?? true),
    isEmailVerified: Boolean(profile.isEmailVerified),
    phone: profile.phone as string | undefined,
    preferences: profile.preferences as User['preferences'],
    avatarUrl: profile.avatarUrl as string | undefined,
    createdAt:
      createdAtRaw instanceof Date
        ? createdAtRaw
        : new Date((createdAtRaw as string) || Date.now()),
    updatedAt:
      updatedAtRaw instanceof Date
        ? updatedAtRaw
        : new Date((updatedAtRaw as string) || Date.now()),
  };
}

/** Mirror DashboardRouter redirect logic for impersonated users. */
export function getImpersonationRedirectPath(user: User): string {
  return resolvePlatformDashboardRedirect(user).path;
}

export async function enterImpersonation(
  userId: string,
  mode: ImpersonationMode = 'read_only'
): Promise<{ redirectPath: string }> {
  const res = await adminBackofficeApi.startImpersonation(userId, mode);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to start impersonation');
  }

  const { sessionId, profile, mode: sessionMode } = res.data as {
    sessionId: string;
    mode: ImpersonationMode;
    profile: Record<string, unknown>;
  };
  const user = mapProfileToUser(profile);

  useImpersonationStore.getState().startImpersonation(sessionId, user, sessionMode);
  useAuthStore.getState().setUser(user);

  const { preloadUserData } = await import('../store/preload');
  await preloadUserData(user);

  return { redirectPath: getImpersonationRedirectPath(user) };
}

export async function exitImpersonation(): Promise<void> {
  const sessionId = useImpersonationStore.getState().sessionId;
  if (sessionId) {
    try {
      await adminBackofficeApi.stopImpersonation(sessionId);
    } catch {
      // Best-effort audit stop; always clear local state
    }
  }

  useImpersonationStore.getState().clearImpersonation();
  await useAuthStore.getState().refreshUser();
}

/** Re-sync impersonation mode from the server (e.g. after page reload). */
export async function revalidateImpersonationSession(): Promise<void> {
  const { sessionId, targetUserId, clearImpersonation, syncImpersonationMode } =
    useImpersonationStore.getState();
  if (!sessionId || !targetUserId) {
    return;
  }

  try {
    const res = await adminBackofficeApi.getImpersonationSession(sessionId);
    if (!res.success || !res.data) {
      clearImpersonation();
      await useAuthStore.getState().refreshUser();
      return;
    }

    const { targetUserId: serverTargetUserId, mode } = res.data as {
      targetUserId: string;
      mode: ImpersonationMode;
    };

    if (serverTargetUserId !== targetUserId) {
      clearImpersonation();
      await useAuthStore.getState().refreshUser();
      return;
    }

    syncImpersonationMode(mode);
  } catch {
    clearImpersonation();
    await useAuthStore.getState().refreshUser();
  }
}
